import React, { useState, useMemo, useEffect } from 'react';
import { Search, RotateCcw } from 'lucide-react';
import clsx from 'clsx';
import {
  AssessmentItem,
  ItemEvaluation,
  ProcessPriority,
  ProcessType,
  PROCESS_TYPES,
} from '@app-types';
import { getProcessTypeLabel } from '@utils/referenceCatalog';
import Button from '@components/common/Button';
import styles from './AssessmentFrameworkFilters.module.css';

const PROCESS_TYPE_BADGE_CLASS: Record<ProcessType, string> = {
  ENT: styles.processTypeEnt,
  OPS: styles.processTypeOps
};

interface FilterState {
  searchTerm: string;
  selectedDomains: string[];
  selectedEvaluations: ItemEvaluation[];
  selectedPriorities: ProcessPriority[];
  selectedProcessTypes: ProcessType[];
}

interface AssessmentFrameworkFiltersProps {
  assessmentItems: AssessmentItem[];
  onFilteredItemsChange: (filteredItems: AssessmentItem[]) => void;
}

const AssessmentFrameworkFilters: React.FC<AssessmentFrameworkFiltersProps> = ({
  assessmentItems,
  onFilteredItemsChange
}) => {
  const [filters, setFilters] = useState<FilterState>({
    searchTerm: '',
    selectedDomains: [],
    selectedEvaluations: [],
    selectedPriorities: [],
    selectedProcessTypes: []
  });

  // Get unique values for filter options
  const filterOptions = useMemo(() => {
    const domains = Array.from(new Set(assessmentItems.map((item) => item.domain_name))).sort((a, b) =>
      a.localeCompare(b)
    );
    const evaluations: ItemEvaluation[] = [
      'Not Assessed',
      'Implemented',
      'Implemented (Needs Evidence)',
      'Gap',
      'Not Applicable',
      'Remediated'
    ];
    const priorities: ProcessPriority[] = ['High', 'Medium', 'Low'];
    return { domains, evaluations, priorities, processTypes: PROCESS_TYPES };
  }, [assessmentItems]);

  // Filter assessment items based on current filters
  const filteredItems = useMemo(() => {
    return assessmentItems.filter((item) => {
      // Search term filter
      if (filters.searchTerm) {
        const searchLower = filters.searchTerm.toLowerCase();
        const matchesSearch =
          (item.process_name || '').toLowerCase().includes(searchLower) ||
          (item.process_description || '').toLowerCase().includes(searchLower) ||
          (item.domain_name || '').toLowerCase().includes(searchLower) ||
          (item.notes || '').toLowerCase().includes(searchLower);
        if (!matchesSearch) return false;
      }

      // Domain filter
      if (filters.selectedDomains.length > 0 && !filters.selectedDomains.includes(item.domain_name)) {
        return false;
      }

      // Evaluation filter
      if (filters.selectedEvaluations.length > 0 && !filters.selectedEvaluations.includes(item.evaluation)) {
        return false;
      }

      // Priority filter
      if (filters.selectedPriorities.length > 0 && !filters.selectedPriorities.includes(item.process_priority)) {
        return false;
      }

      // Process Type filter
      if (filters.selectedProcessTypes.length > 0 && !filters.selectedProcessTypes.includes(item.process_type)) {
        return false;
      }

      return true;
    });
  }, [assessmentItems, filters]);

  // Notify parent component of filtered items after render
  useEffect(() => {
    onFilteredItemsChange(filteredItems);
  }, [filteredItems, onFilteredItemsChange]);

  // Update filter state
  const updateFilter = (key: keyof FilterState, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  // Toggle filter arrays (for multi-select)
  const toggleFilterArray = (
    key: 'selectedDomains' | 'selectedEvaluations' | 'selectedPriorities' | 'selectedProcessTypes',
    value: string
  ) => {
    setFilters((prev) => ({
      ...prev,
      [key]: (prev[key] as string[]).includes(value)
        ? (prev[key] as string[]).filter((item) => item !== value)
        : [...(prev[key] as string[]), value]
    }));
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      searchTerm: '',
      selectedDomains: [],
      selectedEvaluations: [],
      selectedPriorities: [],
      selectedProcessTypes: []
    });
  };

  // Check if any filters are active
  const hasActiveFilters =
    filters.searchTerm ||
    filters.selectedDomains.length > 0 ||
    filters.selectedEvaluations.length > 0 ||
    filters.selectedPriorities.length > 0 ||
    filters.selectedProcessTypes.length > 0;

  // Calculate dynamic counts for each filter option
  const calculateFilterCounts = () => {
    // Helper function to filter items excluding a specific filter type
    const getFilteredItemsExcluding = (excludeFilter: 'domain' | 'evaluation' | 'priority' | 'processType') => {
      return assessmentItems.filter((item) => {
        // Search term filter (always applied)
        if (filters.searchTerm) {
          const searchLower = filters.searchTerm.toLowerCase();
          const matchesSearch =
            (item.process_name || '').toLowerCase().includes(searchLower) ||
            (item.process_description || '').toLowerCase().includes(searchLower) ||
            (item.domain_name || '').toLowerCase().includes(searchLower) ||
            (item.notes || '').toLowerCase().includes(searchLower);
          if (!matchesSearch) return false;
        }

        // Apply other filters except the excluded one
        if (
          excludeFilter !== 'domain' &&
          filters.selectedDomains.length > 0 &&
          !filters.selectedDomains.includes(item.domain_name)
        ) {
          return false;
        }
        if (
          excludeFilter !== 'evaluation' &&
          filters.selectedEvaluations.length > 0 &&
          !filters.selectedEvaluations.includes(item.evaluation)
        ) {
          return false;
        }
        if (
          excludeFilter !== 'priority' &&
          filters.selectedPriorities.length > 0 &&
          !filters.selectedPriorities.includes(item.process_priority)
        ) {
          return false;
        }
        if (
          excludeFilter !== 'processType' &&
          filters.selectedProcessTypes.length > 0 &&
          !filters.selectedProcessTypes.includes(item.process_type)
        ) {
          return false;
        }

        return true;
      });
    };

    // Calculate counts for domains
    const domainCounts: Record<string, number> = {};
    const itemsForDomains = getFilteredItemsExcluding('domain');
    filterOptions.domains.forEach((domain) => {
      domainCounts[domain] = itemsForDomains.filter((item) => item.domain_name === domain).length;
    });

    // Calculate counts for evaluations
    const evaluationCounts: Record<string, number> = {};
    const itemsForEvaluations = getFilteredItemsExcluding('evaluation');
    filterOptions.evaluations.forEach((evaluation) => {
      evaluationCounts[evaluation] = itemsForEvaluations.filter((item) => item.evaluation === evaluation).length;
    });

    // Calculate counts for priorities
    const priorityCounts: Record<string, number> = {};
    const itemsForPriorities = getFilteredItemsExcluding('priority');
    filterOptions.priorities.forEach((priority) => {
      priorityCounts[priority] = itemsForPriorities.filter((item) => item.process_priority === priority).length;
    });

    // Calculate counts for process types
    const processTypeCounts: Partial<Record<ProcessType, number>> = {};
    const itemsForProcessTypes = getFilteredItemsExcluding('processType');
    filterOptions.processTypes.forEach((processType) => {
      processTypeCounts[processType] = itemsForProcessTypes.filter((item) => item.process_type === processType).length;
    });

    return { domainCounts, evaluationCounts, priorityCounts, processTypeCounts };
  };

  const { domainCounts, evaluationCounts, priorityCounts, processTypeCounts } = useMemo(calculateFilterCounts, [
    assessmentItems,
    filters,
    filterOptions.domains,
    filterOptions.evaluations,
    filterOptions.priorities,
    filterOptions.processTypes
  ]);

  // Get evaluation badge class (CSS Module)
  const getEvaluationBadgeClass = (evaluation: ItemEvaluation) => {
    switch (evaluation) {
      case 'Implemented':
        return 'evalImplemented';
      case 'Gap':
        return 'evalGap';
      case 'Not Applicable':
        return 'evalNotApplicable';
      case 'Not Assessed':
        return 'evalNotAssessed';
      case 'Remediated':
        return 'evalRemediated';
      default:
        return 'evalNotApplicable';
    }
  };

  // Get priority badge class (CSS Module)
  const getPriorityBadgeClass = (priority: ProcessPriority) => {
    switch (priority) {
      case 'High':
        return 'priorityHigh';
      case 'Medium':
        return 'priorityMedium';
      case 'Low':
        return 'priorityLow';
      default:
        return 'priorityMedium';
    }
  };

  return (
    <div className={styles.frameworkFilters}>
      <div className={styles.frameworkFiltersHorizontal}>
        {/* Search Filter */}
        <div className={clsx(styles.frameworkFilterGroup, styles.searchGroup)}>
          <label htmlFor="framework-filter-search" className={styles.frameworkFilterLabel}>Search</label>
          <div className={styles.frameworkSearchContainer}>
            <Search size={16} className={styles.frameworkSearchIcon} />
            <input
              id="framework-filter-search"
              type="text"
              className={styles.frameworkSearchInput}
              placeholder="Search processes, domains, or notes..."
              value={filters.searchTerm}
              onChange={(e) => updateFilter('searchTerm', e.target.value)}
            />
          </div>
        </div>

        {/* Domain Filter */}
        <div className={styles.frameworkFilterGroup}>
          <label className={styles.frameworkFilterLabel}>
            Domains
            {filters.selectedDomains.length > 0 && (
              <span className={styles.frameworkFilterCount}>({filters.selectedDomains.length})</span>
            )}
          </label>
          <div className={styles.frameworkFilterBadges}>
            {filterOptions.domains.map((domain) => (
              <button
                key={domain}
                className={clsx(
                  styles.frameworkFilterBadge,
                  styles.domain,
                  filters.selectedDomains.includes(domain) && styles.selected
                )}
                onClick={() => toggleFilterArray('selectedDomains', domain)}
              >
                {domain} ({domainCounts[domain] || 0})
              </button>
            ))}
          </div>
        </div>

        {/* Evaluation Status Filter */}
        <div className={styles.frameworkFilterGroup}>
          <label className={styles.frameworkFilterLabel}>
            Evaluation Status
            {filters.selectedEvaluations.length > 0 && (
              <span className={styles.frameworkFilterCount}>({filters.selectedEvaluations.length})</span>
            )}
          </label>
          <div className={styles.frameworkFilterBadges}>
            {filterOptions.evaluations.map((evaluation) => (
              <button
                key={evaluation}
                className={clsx(
                  styles.frameworkFilterBadge,
                  styles[getEvaluationBadgeClass(evaluation)],
                  filters.selectedEvaluations.includes(evaluation) && styles.selected
                )}
                onClick={() => toggleFilterArray('selectedEvaluations', evaluation)}
              >
                {evaluation} ({evaluationCounts[evaluation] || 0})
              </button>
            ))}
          </div>
        </div>

        {/* Priority Filter */}
        <div className={styles.frameworkFilterGroup}>
          <label className={styles.frameworkFilterLabel}>
            Priority
            {filters.selectedPriorities.length > 0 && (
              <span className={styles.frameworkFilterCount}>({filters.selectedPriorities.length})</span>
            )}
          </label>
          <div className={styles.frameworkFilterBadges}>
            {filterOptions.priorities.map((priority) => (
              <button
                key={priority}
                className={clsx(
                  styles.frameworkFilterBadge,
                  styles[getPriorityBadgeClass(priority)],
                  filters.selectedPriorities.includes(priority) && styles.selected
                )}
                onClick={() => toggleFilterArray('selectedPriorities', priority)}
              >
                {priority} Priority ({priorityCounts[priority] || 0})
              </button>
            ))}
          </div>
        </div>

        {/* Process Type Filter */}
        <div className={styles.frameworkFilterGroup}>
          <label className={styles.frameworkFilterLabel}>
            Process Type
            {filters.selectedProcessTypes.length > 0 && (
              <span className={styles.frameworkFilterCount}>({filters.selectedProcessTypes.length})</span>
            )}
          </label>
          <div className={styles.frameworkFilterBadges}>
            {filterOptions.processTypes.map((processType) => (
              <button
                key={processType}
                className={clsx(
                  styles.frameworkFilterBadge,
                  PROCESS_TYPE_BADGE_CLASS[processType],
                  filters.selectedProcessTypes.includes(processType) && styles.selected
                )}
                title={getProcessTypeLabel(processType)}
                onClick={() => toggleFilterArray('selectedProcessTypes', processType)}
              >
                {processType} ({processTypeCounts[processType] || 0})
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Filter Results Summary */}
      <div className={styles.frameworkFilterSummary}>
        <span className={styles.frameworkResultsCount}>
          {filteredItems.length} of {assessmentItems.length} processes
          {hasActiveFilters && ' (filtered)'}
        </span>
        {hasActiveFilters && (
          <div className={styles.frameworkClearFilters}>
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              <RotateCcw size={16} />
              Clear Filters
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AssessmentFrameworkFilters;
